/*
 * ------------------------------------------------------------
 * Cálculo de frete por CEP no carrinho (drawer e página /cart).
 *
 * Fluxo em duas chamadas, em série:
 *   1. ViaCEP  -> https://viacep.com.br/ws/<8 dígitos>/json/   (CEP -> UF)
 *   2. Shopify -> /cart/shipping_rates.json?shipping_address[zip]=...
 *                 &shipping_address[country]=Brazil&shipping_address[province]=<UF>
 *
 * A taxa escolhida é informativa: nada é enviado à Shopify e o checkout
 * recalcula do zero.
 *
 * Restrição do tema: o drawer do Dawn é destruído e recriado via innerHTML a
 * cada mutação do carrinho (product-form.js -> renderContents, cart.js ->
 * updateQuantity / onCartUpdate). Por isso o estado vive em module scope +
 * localStorage, nunca na instância do elemento, e o Liquid reentrega a
 * assinatura do carrinho em data-cart-signature a cada re-render.
 * ------------------------------------------------------------
 */

/* Escopo próprio: os assets do tema são scripts clássicos, não módulos, então
   tudo declarado no topo do arquivo cairia no escopo global — onde colide com
   qualquer outro asset que use os mesmos nomes, e o segundo a carregar morre
   no parse com "has already been declared". */
(function () {
const CEP_STORAGE_KEY = 'userCep';
const RATE_STORAGE_KEY = 'selectedShippingRate';

const SHIPPING_MESSAGES = {
  invalidCep: 'CEP inválido',
  notFound: 'CEP não encontrado',
  requestError: 'Não foi possível calcular o frete. Tente novamente.',
  noRates: 'Não há opções de envio disponíveis para este CEP',
  toBeCalculated: 'A calcular',
  free: 'Grátis',
  optionsLabel: 'Escolha uma opção de frete',
  deliveryPrefix: 'Chega até',
};

/* Estado compartilhado entre instâncias e entre re-renders do drawer. */
const shippingState = {
  signature: null, // "item_count:total_price" da cotação atual
  rates: [],
  selectedName: null,
  quoted: false, // já cotou ao menos uma vez nesta sessão de página
};

let fetchToken = 0;

/* Na página /cart com o drawer ativo existem duas instâncias no DOM, cada uma
   com o seu próprio .totals. Elas compartilham shippingState, mas cada uma
   precisa repintar o seu total quando a outra muda a seleção. */
const SELECTION_CHANGED_EVENT = 'cart-shipping:selection-changed';

/* Assinatura cujo recálculo automático já foi disparado por alguma instância. */
let requotedSignature = null;

function onlyDigits(value) {
  return String(value || '').replace(/\D/g, '');
}

function maskCep(value) {
  const digits = onlyDigits(value).substring(0, 8);
  return digits.length > 5 ? `${digits.substring(0, 5)}-${digits.substring(5)}` : digits;
}

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    return null;
  }
}

function writeStorage(key, value) {
  try {
    if (value === null) localStorage.removeItem(key);
    else localStorage.setItem(key, value);
  } catch (error) {
    /* Modo privado ou quota estourada: seguir sem persistir. */
  }
}

function escapeHtml(value) {
  return String(value == null ? '' : value).replace(
    /[&<>"']/g,
    (char) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[char])
  );
}

function capitalize(text) {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function withDelimiters(cents, precision, thousands, decimal) {
  const amount = (Math.abs(cents) / 100).toFixed(precision);
  const parts = amount.split('.');
  const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
  const sign = cents < 0 ? '-' : '';
  return parts[1] ? `${sign}${integer}${decimal}${parts[1]}` : `${sign}${integer}`;
}

/* Equivalente mínimo do Shopify.formatMoney, que este tema não carrega. */
function formatMoney(cents, format) {
  const value = Number(cents) || 0;
  if (!format) return withDelimiters(value, 2, '.', ',');

  const patterns = {
    amount: () => withDelimiters(value, 2, ',', '.'),
    amount_no_decimals: () => withDelimiters(value, 0, ',', '.'),
    amount_with_comma_separator: () => withDelimiters(value, 2, '.', ','),
    amount_no_decimals_with_comma_separator: () => withDelimiters(value, 0, '.', ','),
    amount_with_apostrophe_separator: () => withDelimiters(value, 2, "'", '.'),
    amount_with_space_separator: () => withDelimiters(value, 2, ' ', ','),
    amount_no_decimals_with_space_separator: () => withDelimiters(value, 0, ' ', ','),
  };

  return String(format).replace(/\{\{\s*(\w+)\s*\}\}/g, (match, name) =>
    patterns[name] ? patterns[name]() : match
  );
}

/* /cart/shipping_rates.json devolve price como string em reais ("19.90"). */
function rateCents(rate) {
  return Math.round(Number(rate.price) * 100) || 0;
}

function formatDeliveryDate(isoDate) {
  const [year, month, day] = String(isoDate).split('T')[0].split('-').map(Number);
  if (!year || !month || !day) return '';

  const date = new Date(year, month - 1, day);
  const weekday = new Intl.DateTimeFormat('pt-BR', { weekday: 'long' }).format(date);
  const dayLabel = new Intl.DateTimeFormat('pt-BR', { day: '2-digit' }).format(date);
  const monthLabel = new Intl.DateTimeFormat('pt-BR', { month: 'long' }).format(date);

  return `${SHIPPING_MESSAGES.deliveryPrefix} ${capitalize(weekday)} (${dayLabel} de ${capitalize(monthLabel)})`;
}

/* Retirada na loja devolve "Título|Endereço: ...|Horário"; só a primeira parte é título. */
function rateNameParts(rate) {
  const parts = String(rate.name).split('|').map((part) => part.trim());
  return { title: parts[0], details: parts.slice(1).filter(Boolean) };
}

function isPickupName(name) {
  return /retirada|pickup/i.test(rateNameParts({ name }).title);
}

function defaultRateIndex(rates) {
  const delivery = rates.findIndex((rate) => !isPickupName(rate.name));
  return delivery >= 0 ? delivery : 0;
}

function rateDescription(rate) {
  const details = rateNameParts(rate).details;
  if (details.length) return details.join(' · ');
  if (rate.description) return rate.description;

  const range = Array.isArray(rate.delivery_range) ? rate.delivery_range[rate.delivery_range.length - 1] : null;
  const iso = rate.delivery_date || range;
  return iso ? formatDeliveryDate(iso) : '';
}

class CartShippingEstimator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.cart-summary__field');
    this.input = this.querySelector('input[name="cep"]');
    this.submitButton = this.querySelector('.cart-summary__submit');
    this.response = this.querySelector('[data-shipping-response]');
    this.valueNode = this.querySelector('[data-shipping-value]');
    this.toggle = this.querySelector('.cart-summary__toggle');
    this.dropdown = this.querySelector('.cart-summary__dropdown');

    if (!this.form || !this.input || !this.response) return;

    this.onSubmit = this.handleSubmit.bind(this);
    this.onInput = this.handleInput.bind(this);
    this.onRateChange = this.handleRateChange.bind(this);
    this.onSelectionChanged = () => this.updateTotals();

    this.form.addEventListener('submit', this.onSubmit);
    this.input.addEventListener('input', this.onInput);
    this.response.addEventListener('change', this.onRateChange);
    document.addEventListener(SELECTION_CHANGED_EVENT, this.onSelectionChanged);

    this.hydrate();
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.input?.removeEventListener('input', this.onInput);
    this.response?.removeEventListener('change', this.onRateChange);
    document.removeEventListener(SELECTION_CHANGED_EVENT, this.onSelectionChanged);
  }

  get signature() {
    return this.dataset.cartSignature || '';
  }

  get cartTotal() {
    return parseInt(this.dataset.cartTotal, 10) || 0;
  }

  get itemCount() {
    return parseInt(this.dataset.itemCount, 10) || 0;
  }

  get hasShippableItems() {
    return this.itemCount > 0 && this.dataset.requiresShipping === 'true';
  }

  get totalNode() {
    return this.closest('cart-summary')?.querySelector('.cart-summary__value--total') || null;
  }

  get selectedRate() {
    if (shippingState.signature !== this.signature) return null;
    if (!shippingState.selectedName) return null;
    return shippingState.rates.find((rate) => rate.name === shippingState.selectedName) || null;
  }

  hydrate() {
    const storedCep = readStorage(CEP_STORAGE_KEY);
    if (storedCep && !this.input.value) this.input.value = storedCep;

    if (!shippingState.selectedName) {
      const storedRate = readStorage(RATE_STORAGE_KEY);
      if (storedRate && isPickupName(storedRate)) writeStorage(RATE_STORAGE_KEY, null);
      else shippingState.selectedName = storedRate;
    }

    /* Carrinho intacto: repinta de memória, sem rede. Cobre reabrir o drawer. */
    if (shippingState.signature === this.signature && shippingState.rates.length) {
      this.renderRates(shippingState.rates);
      this.setExpanded(true);
      return;
    }

    /* Carrinho mudou: a cotação anterior está velha. */
    const shouldRequote =
      shippingState.quoted &&
      requotedSignature !== this.signature &&
      onlyDigits(this.input.value).length === 8 &&
      this.hasShippableItems;

    shippingState.rates = [];
    shippingState.signature = null;
    this.response.innerHTML = '';
    this.updateTotals();

    if (shouldRequote) {
      requotedSignature = this.signature;
      this.setExpanded(true);
      this.calculate();
    }
  }

  /* Sem listener: quem trata o clique é o <cart-summary>. Isto existe só para
     reabrir o dropdown ao restaurar uma cotação da memória. */
  setExpanded(expanded) {
    this.toggle?.setAttribute('aria-expanded', String(expanded));
    this.dropdown?.toggleAttribute('open', expanded);
  }

  handleInput(event) {
    const masked = maskCep(event.target.value);
    event.target.value = masked;
    if (onlyDigits(masked).length === 8) writeStorage(CEP_STORAGE_KEY, masked);
  }

  handleSubmit(event) {
    event.preventDefault();
    this.calculate();
  }

  handleRateChange(event) {
    const input = event.target.closest('.cart-shipping__rate-input');
    if (!input) return;
    this.selectRate(Number(input.value), true);
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.classList.toggle('loading', isLoading);
    this.submitButton.disabled = isLoading;
  }

  showError(message) {
    shippingState.rates = [];
    shippingState.signature = null;
    this.response.innerHTML = `<p class="cart-shipping__error">${escapeHtml(message)}</p>`;
    this.updateTotals();
    this.notifyOthers();
  }

  /* updateTotals() é idempotente e não dispara nada, então reentrar aqui pela
     própria escuta do documento não gera laço. */
  notifyOthers() {
    document.dispatchEvent(new CustomEvent(SELECTION_CHANGED_EVENT));
  }

  async calculate() {
    const digits = onlyDigits(this.input.value);
    if (digits.length !== 8) {
      this.showError(SHIPPING_MESSAGES.invalidCep);
      return;
    }
    if (!this.hasShippableItems) {
      this.showError(this.dataset.emptyMessage || SHIPPING_MESSAGES.requestError);
      return;
    }

    const token = ++fetchToken;
    this.setLoading(true);

    try {
      const zip = await this.lookupZip(digits);
      if (token !== fetchToken) return;
      if (!zip) {
        this.showError(SHIPPING_MESSAGES.notFound);
        return;
      }

      const rates = await this.fetchRates(zip);
      if (token !== fetchToken) return;
      if (!rates.length) {
        this.showError(SHIPPING_MESSAGES.noRates);
        return;
      }

      shippingState.rates = rates.slice().sort((a, b) => rateCents(a) - rateCents(b));
      shippingState.signature = this.signature;
      shippingState.quoted = true;
      this.renderRates(shippingState.rates);
    } catch (error) {
      if (token !== fetchToken) return;
      console.error('Erro ao calcular o frete:', error);
      this.showError(SHIPPING_MESSAGES.requestError);
    } finally {
      if (token === fetchToken) this.setLoading(false);
    }
  }

  async lookupZip(digits) {
    const response = await fetch(`https://viacep.com.br/ws/${digits}/json/`);
    if (!response.ok) throw new Error(`ViaCEP respondeu ${response.status}`);

    const data = await response.json();
    /* O ViaCEP responde 200 com { "erro": true } para CEP inexistente. */
    if (!data || data.erro || !data.uf) return null;

    return { cep: data.cep || maskCep(digits), uf: data.uf };
  }

  async fetchRates(zip) {
    const params = new URLSearchParams({
      'shipping_address[zip]': zip.cep,
      'shipping_address[country]': 'Brazil',
      'shipping_address[province]': zip.uf,
    });
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';

    const response = await fetch(`${cartUrl}/shipping_rates.json?${params}`);
    if (!response.ok) throw new Error(`shipping_rates respondeu ${response.status}`);

    const data = await response.json();
    return Array.isArray(data.shipping_rates) ? data.shipping_rates : [];
  }

  renderRates(rates) {
    const group = `cart-shipping-rate-${this.dataset.idSuffix || 'default'}`;
    const storedIndex = shippingState.selectedName
      ? rates.findIndex((rate) => rate.name === shippingState.selectedName)
      : -1;
    const selectedIndex = storedIndex >= 0 ? storedIndex : defaultRateIndex(rates);

    const items = rates
      .map((rate, index) => {
        const id = `${group}-${index}`;
        return `<li class="cart-shipping__rate">
            <input
              class="cart-shipping__rate-input"
              type="radio"
              name="${group}"
              id="${id}"
              value="${index}"
              ${index === selectedIndex ? 'checked' : ''}
            >
            <label class="cart-shipping__rate-label" for="${id}">
              <span class="cart-shipping__rate-name">${escapeHtml(this.rateTitle(rate))}</span>
              <span class="cart-shipping__rate-description">${escapeHtml(rateDescription(rate))}</span>
            </label>
          </li>`;
      })
      .join('');

    this.response.innerHTML = `<fieldset class="cart-shipping__rates-group">
        <legend class="visually-hidden">${escapeHtml(SHIPPING_MESSAGES.optionsLabel)}</legend>
        <ul class="cart-shipping__rates list-unstyled">${items}</ul>
      </fieldset>`;

    this.selectRate(selectedIndex);
  }

  rateTitle(rate) {
    const cents = rateCents(rate);
    const { title } = rateNameParts(rate);
    if (cents > 0) return `${title} - ${formatMoney(cents, this.dataset.moneyFormat)}`;

    const normalized = title.toLowerCase();
    if (normalized.includes('grátis') || normalized.includes('gratis') || normalized.includes('free')) {
      return title;
    }
    return `${title} - ${SHIPPING_MESSAGES.free}`;
  }

  selectRate(index, persist = false) {
    const rate = shippingState.rates[index];
    if (!rate) return;

    shippingState.selectedName = rate.name;
    if (persist) writeStorage(RATE_STORAGE_KEY, rate.name);
    this.updateTotals();
    this.notifyOthers();
  }

  updateTotals() {
    const rate = this.selectedRate;
    const cents = rate ? rateCents(rate) : 0;

    if (this.valueNode) {
      if (!rate) this.valueNode.textContent = SHIPPING_MESSAGES.toBeCalculated;
      else if (cents === 0) this.valueNode.textContent = SHIPPING_MESSAGES.free;
      else this.valueNode.textContent = formatMoney(cents, this.dataset.moneyFormat);
    }

    this.toggleAttribute('data-shipping-selected', Boolean(rate));

    if (!rate && !this.totalPatched) return;

    const totalNode = this.totalNode;
    if (!totalNode) return;

    totalNode.textContent = formatMoney(this.cartTotal + cents, this.dataset.moneyFormat);
    this.totalPatched = Boolean(rate);
  }
}

customElements.define('cart-shipping-estimator', CartShippingEstimator);
})();
